import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  toast,
} from '@parcel/ui';
import { UserSearch } from 'lucide-react';
import * as React from 'react';
import { apiErrorMessage } from '../../lib/api.js';
import {
  type ApiRoleFamily,
  PEOPLE_SEARCH_FRESH_DAYS,
  PEOPLE_SEARCH_LIMIT,
  personasPhrase,
  searchCostLine,
  searchResultLine,
  shortDate,
  usePeopleSearch,
  windowEnd,
} from '../../lib/enrich.js';
import { InsufficientCreditsNotice } from '../enrich/credits-notice.js';
import { useEnrichScope } from '../enrich/enrich-scope.js';
import { Tip } from '../enrich/reveal-button.js';

export function FindPeopleButton({
  accountId,
  accountName,
  personas,
  price,
  searchedAt = null,
}: {
  accountId: string;
  accountName: string;
  personas: readonly ApiRoleFamily[];
  price: number;
  /** When the provider was last asked for people here; a search before 30 days later only repeats it. */
  searchedAt?: string | null;
}) {
  const until = searchedAt ? windowEnd(searchedAt, PEOPLE_SEARCH_FRESH_DAYS) : null;
  if (searchedAt && until && until.getTime() > Date.now()) {
    return (
      <Tip tip={`Prospect again ${shortDate(until.toISOString())}.`}>
        <span
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
          data-testid="find-people-cooldown"
        >
          <UserSearch className="size-3.5" aria-hidden />
          {`Last prospected ${shortDate(searchedAt)}`}
        </span>
      </Tip>
    );
  }
  return (
    <FindPeopleDialog
      accountId={accountId}
      accountName={accountName}
      personas={personas}
      price={price}
    />
  );
}

function FindPeopleDialog({
  accountId,
  accountName,
  personas,
  price,
}: {
  accountId: string;
  accountName: string;
  personas: readonly ApiRoleFamily[];
  price: number;
}) {
  const { workspaceId } = useEnrichScope();
  const search = usePeopleSearch(workspaceId);
  const [open, setOpen] = React.useState(false);
  const [refused, setRefused] = React.useState<'credits' | 'spend_limit' | null>(null);

  const run = () =>
    search.mutate(
      { accountId, families: personas },
      {
        onSuccess: (result) => {
          if (result.kind === 'insufficient_credits' || result.kind === 'spend_limit') {
            setRefused(result.kind === 'spend_limit' ? 'spend_limit' : 'credits');
            return;
          }
          setOpen(false);
          toast.success(searchResultLine(result));
        },
        onError: (error) =>
          toast.error(apiErrorMessage(error, "Couldn't search for people. Try again.")),
      },
    );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (search.isPending) return;
        setOpen(next);
        setRefused(null);
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8">
          Find more people
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Find more people at {accountName}</DialogTitle>
          <DialogDescription>
            Searches for {personasPhrase(personas)} at {accountName}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1 text-sm">
          <p className="font-medium">{searchCostLine(price, PEOPLE_SEARCH_LIMIT)}</p>
          <p className="text-muted-foreground">
            Charged only for people not already listed at this firm.
          </p>
        </div>
        {refused ? <InsufficientCreditsNotice reason={refused} /> : null}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={search.isPending}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button type="button" disabled={search.isPending} onClick={run}>
            {search.isPending ? 'Searching…' : 'Find people'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
